import { Component, OnInit, inject } from "@angular/core";
import { AppTableComponent } from "../../../../Common/Components/Table/TableComponent";
import {
  TableActionConfig,
  TableColumn,
  TableConfig,
  TablePagination,
  TableSearchEvent,
  TableSort,
  TableDateRangeEvent,
} from "../../../../Common/Components/Table/Type/TableType";
import { SessionWithDeviceInfo } from "../../Dto/SessionModel";
import { SessionEffects } from "../../State/SessionEffect";
import { SessionState } from "../../State/SessionState";
import { ConfirmModalEffects } from "../../../../Common/Components/ConfirmModal/State/ConfirmModalEffects";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { formatDateNumeric } from "../../../../Common/Utility/UtilityDate";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-session-home",
  standalone: true,
  imports: [AppTableComponent],
  templateUrl: "./SessionHomePage.html",
  styleUrl: "./SessionHomePage.scss",
})
export class SessionHomePage implements OnInit {
  private readonly sessionEffects = inject(SessionEffects);
  readonly sessionState = inject(SessionState);
  private readonly confirmModalEffects = inject(ConfirmModalEffects);
  private readonly toastEffects = inject(ToastEffects);

  sessionColumns: TableColumn<SessionWithDeviceInfo>[] = [
    { key: "id", header: "ID", sortable: true, width: "10%", visibleByDefault: false },
    {
      key: "user.name",
      header: "User",
      sortable: true,
      width: "15%",
      cellTemplate: (item) => item.user.name,
    },
    {
      key: "user.email",
      header: "Email",
      sortable: true,
      width: "25%",
      cellTemplate: (item) => item.user.email,
    },
    {
      key: "device.browser",
      header: "Browser",
      sortable: true,
      width: "15%",
      cellTemplate: (item) => item.device.browser,
    },
    {
      key: "device.os",
      header: "OS",
      sortable: true,
      width: "15%",
      cellTemplate: (item) => item.device.os,
    },
    {
      key: "expireAt",
      header: "Expired At",
      sortable: true,
      width: "20%",
      cellTemplate: (item) => formatDateNumeric(item.expireAt),
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      width: "20%",
      cellTemplate: (item) => formatDateNumeric(item.createdAt),
    },
  ];

  get sessionConfig(): TableConfig<SessionWithDeviceInfo> {
    return {
      loading: this.sessionState.loading(),
      emptyMessage: "No sessions found",
      showActions: true,
      showSearch: true,
      searchPlaceholder: "Search sessions...",
      showCreate: false,
      showDateRangeFilter: true,
      dateRangeFilterLabel: "Filter by session date",
      actionButtons: [
        {
          icon: "delete",
          label: "Delete",
          handler: (item: SessionWithDeviceInfo) => this.deleteSession(item),
          visible: () => true,
        },
      ],
    };
  }

  get sessionPagination(): TablePagination {
    return this.sessionState.pagination();
  }

  ngOnInit(): void {
    this.sessionEffects.searchSessions({}).catch(() => undefined);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSortChange(sort: TableSort | null): void {
    // Handle sort change if needed
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onActionClick<T>(event: { action: TableActionConfig<T>; item: T }): void {
    // Removed logging
  }

  onPageChange(event: { page: number; search?: string }): void {
    const currentSearchParams = this.sessionState.searchParams();
    this.sessionEffects.searchSessions({ ...currentSearchParams, page: event.page, search: event.search }).catch(() => undefined);
  }

  onPageSizeChange(event: { limit: number; search?: string }): void {
    const currentSearchParams = this.sessionState.searchParams();
    this.sessionEffects
      .searchSessions({ ...currentSearchParams, page: 1, limit: event.limit, search: event.search })
      .catch(() => undefined);
  }

  onSearchChange(event: TableSearchEvent): void {
    this.sessionEffects.searchSessions({ search: event.term, page: event.page }).catch(() => undefined);
  }

  onDateRangeChange(event: TableDateRangeEvent): void {
    const currentSearchParams = this.sessionState.searchParams();
    this.sessionEffects
      .searchSessions({
        ...currentSearchParams,
        page: event.page,
        createdAtFrom: event.dateRange.from,
        createdAtTo: event.dateRange.to,
      })
      .catch(() => undefined);
  }

  deleteSession(session: SessionWithDeviceInfo): void {
    this.confirmModalEffects.open({
      title: "Delete session?",
      message: `This will permanently delete the session for user "${session.user.name}". This action cannot be undone.`,
      confirmText: "Delete session",
      cancelText: "Cancel",
      variant: "danger",
      closeOnOverlay: true,
      onConfirm: async () => {
        try {
          await this.sessionEffects.deleteSession(session.id);
          this.toastEffects.show("Session deleted", "success");
        } catch (exception: unknown) {
          this.toastEffects.show(getBackendErrorMessage(exception, "Failed to delete session"), "error");
        }
      },
    });
  }
}
